import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Drawer,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ScheduleSendOutlinedIcon from '@mui/icons-material/ScheduleSendOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import NorthEastOutlinedIcon from '@mui/icons-material/NorthEastOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CurrencyExchangeOutlinedIcon from '@mui/icons-material/CurrencyExchangeOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ROUTES } from '../../../types/global/routes.types';
import { useDispatch, useSelector } from 'react-redux';
import { PeopleSearch } from '../../../components/PeopleSearch/PeopleSearch';
import type { IUserSummary } from '../../../types/global/helper';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    inviteBrandOpsTeam,
    fetchBrandOpsTeam,
    removeBrandOpsTeamMember,
    resendBrandOpsTeamInvite,
    updateBrandOpsTeamMember,
} from '../../../redux/brandOps/actions';
import type { BrandOpsRole, IBrandOpsTeamMember } from '../../../redux/brandOps/types';
import { brandOpsFetchingTeamSelector, brandOpsTeamSelector } from '../../../redux/brandOps/selectors';
import { activeBrandDashboardBrandSelector, walletDashboardSelector } from '../../../redux/buzzbridge/selectors';
import { getWalletDashboard } from '../../../redux/buzzbridge/actions';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import {
    BRAND_LUXE_MUTED,
    BRAND_LUXE_PRIMARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGH,
    BRAND_SURFACE_CONTAINER_LOW,
    brandModalOutlinedInputSx,
} from '../brandDashboardTokens';
import WalletPaymentSection from '../WalletPaymentSection';
import { useWalletFundOptions } from '../../../hook/useWalletFundOptions';
import { findMatchingPreset, getMinWalletTopUpCents } from '../walletFundOptionsUtils';
import { formatUsdCents } from '../campaignDetail/ugcCampaignFundBreakdown';
import { BrandManageGate, ManageGoldButton, MANAGE_PAGE_SX, StatusChip } from './manageShared';

// ── tokens ────────────────────────────────────────────────────────────────────
const C_BORDER = 'rgba(255,255,255,0.07)';
const C_AMBER = BRAND_LUXE_PRIMARY;
const C_MINT = '#00fdc1';
const C_ERROR = '#ff7351';

const FALLBACK_MIN_TOPUP_CENTS = 10_000;

function parseShortfall(error: unknown): { shortfall_cents: number; wallet_balance_cents?: number } | null {
    const err = error as { response?: { data?: { data?: Record<string, unknown> } }; data?: any };
    const body = err?.response?.data ?? err?.data;
    const nested = body?.data as Record<string, unknown> | undefined;
    if (nested && typeof nested.shortfall_cents === 'number') {
        return {
            shortfall_cents: nested.shortfall_cents as number,
            wallet_balance_cents: nested.wallet_balance_cents as number | undefined,
        };
    }
    return null;
}

// ── Role config ────────────────────────────────────────────────────────────────
const ROLES: { value: BrandOpsRole; label: string }[] = [
    { value: 'content_ops_manager', label: 'Content Ops Manager' },
    { value: 'content_strategist', label: 'Content Strategist' },
    { value: 'brand_admin', label: 'Brand Admin' },
    { value: 'viewer', label: 'Viewer' },
];

const ROLE_CARDS: { value: BrandOpsRole; label: string; sub: string }[] = [
    { value: 'content_ops_manager', label: 'Content Ops', sub: 'Manages creator queues, approvals & publishing.' },
    { value: 'content_strategist', label: 'Strategist', sub: 'Curates briefs, trends, and content pacing.' },
    { value: 'brand_admin', label: 'Brand Admin', sub: 'Full admin access and brand identity control.' },
    { value: 'viewer', label: 'Viewer', sub: 'Read-only access, no actions.' },
];

const ROLE_META: Record<BrandOpsRole, { color: string; bg: string; label: string }> = {
    content_ops_manager: { color: C_AMBER, bg: 'rgba(255,201,101,0.15)', label: 'Ops Manager' },
    content_strategist: { color: '#64b4ff', bg: 'rgba(100,180,255,0.15)', label: 'Strategist' },
    brand_admin: { color: '#b47bff', bg: 'rgba(180,123,255,0.15)', label: 'Brand Admin' },
    viewer: { color: '#aaaaaa', bg: 'rgba(170,170,170,0.12)', label: 'Viewer' },
};

// ── Sub-components ─────────────────────────────────────────────────────────────
function RoleBadge({ role }: { role: BrandOpsRole }) {
    const meta = ROLE_META[role] ?? ROLE_META.viewer;
    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.25,
                py: 0.35,
                borderRadius: 5,
                bgcolor: meta.bg,
                border: `1px solid ${meta.color}33`,
            }}>
            <Typography sx={{ fontSize: 11, color: meta.color, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                {meta.label}
            </Typography>
        </Box>
    );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
    return (
        <Typography
            sx={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: 11.5,
                mb: 0.75,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontFamily: FONT_ENUM.MAIN_BOLD,
            }}>
            {children}
        </Typography>
    );
}

// ── Metric card ───────────────────────────────────────────────────────────────
function MetricCard({
    icon,
    label,
    value,
    sub,
    accentColor,
    trend,
}: {
    icon: React.ReactNode;
    label: string;
    value: string | number;
    sub: string;
    accentColor: string;
    trend?: string;
}) {
    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 140,
                p: '18px 20px',
                borderRadius: 2.5,
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                border: `1px solid ${C_BORDER}`,
                position: 'relative',
                overflow: 'hidden',
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography
                    sx={{
                        color: 'rgba(255,255,255,0.45)',
                        fontSize: 11,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                    }}>
                    {label}
                </Typography>
                <Box
                    sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 1.5,
                        bgcolor: BRAND_SURFACE_CONTAINER,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: accentColor,
                    }}>
                    {icon}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                <Typography
                    sx={{
                        color: '#fff',
                        fontSize: 44,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        lineHeight: 1,
                        letterSpacing: '-1px',
                    }}>
                    {value}
                </Typography>
                {trend && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <NorthEastOutlinedIcon sx={{ fontSize: 13, color: C_MINT }} />
                        <Typography sx={{ color: C_MINT, fontSize: 11, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            {trend}
                        </Typography>
                    </Box>
                )}
            </Box>
            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11, mt: 0.5 }}>{sub}</Typography>
            {/* bottom accent */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    background: `linear-gradient(90deg, ${accentColor}50 0%, transparent 100%)`,
                }}
            />
        </Box>
    );
}

// ── Member card (right panel) ─────────────────────────────────────────────────
function MemberCard({
    member,
    resending,
    onOpen,
    onResend,
    onRemove,
}: {
    member: IBrandOpsTeamMember;
    resending: boolean;
    onOpen: () => void;
    onResend: () => void;
    onRemove: () => void;
}) {
    const m = member;
    const roleMeta = ROLE_META[m.role] ?? ROLE_META.viewer;
    const displayName = [m.user?.firstName, m.user?.lastName].filter(Boolean).join(' ') || m.user?.userName || m.email;
    const feeDisplay =
        m.contract?.fee != null
            ? `${m.contract.currency === 'ngn' ? '₦' : '$'}${m.contract.fee.toLocaleString()}`
            : null;

    return (
        <Box
            sx={{
                p: '16px',
                borderRadius: 2,
                bgcolor: BRAND_SURFACE_CONTAINER,
                border: `1px solid ${m.status === 'pending' ? 'rgba(255,201,101,0.2)' : C_BORDER}`,
                transition: 'border-color 0.15s',
                '&:hover': { borderColor: `${roleMeta.color}44` },
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}>
            {/* Top row: avatar + name + status + rate */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ position: 'relative' }}>
                        <Avatar
                            src={m.user?.profile_picture}
                            sx={{
                                width: 44,
                                height: 44,
                                bgcolor: roleMeta.bg,
                                color: roleMeta.color,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 16,
                            }}>
                            {(m.user?.firstName || m.user?.userName || m.email || '?')[0]?.toUpperCase()}
                        </Avatar>
                        {m.status === 'active' && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 1,
                                    right: 1,
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: C_MINT,
                                    border: `2px solid ${BRAND_SURFACE_CONTAINER}`,
                                }}
                            />
                        )}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                            <Typography
                                sx={{
                                    color: '#fff',
                                    fontSize: 16,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    letterSpacing: '-0.2px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 140,
                                }}>
                                {displayName}
                            </Typography>
                            <StatusChip
                                label={m.status}
                                tone={m.status === 'active' ? 'mint' : m.status === 'pending' ? 'gold' : 'muted'}
                            />
                        </Box>
                        <Typography
                            sx={{
                                color: BRAND_LUXE_MUTED,
                                fontSize: 11.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 160,
                            }}>
                            {m.email}
                        </Typography>
                    </Box>
                </Box>
                {feeDisplay && (
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        <Typography
                            sx={{
                                color: C_AMBER,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 19,
                                lineHeight: 1,
                                letterSpacing: '-0.3px',
                            }}>
                            {feeDisplay}
                        </Typography>
                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11 }}>/mo</Typography>
                    </Box>
                )}
            </Box>

            {/* Metadata grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box sx={{ p: '10px 12px', borderRadius: 1.5, bgcolor: BRAND_SURFACE_CONTAINER_LOW }}>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.35)',
                            fontSize: 10,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            mb: 0.5,
                        }}>
                        Role
                    </Typography>
                    <RoleBadge role={m.role} />
                </Box>
                <Box sx={{ p: '10px 12px', borderRadius: 1.5, bgcolor: BRAND_SURFACE_CONTAINER_LOW }}>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.35)',
                            fontSize: 10,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            mb: 0.5,
                        }}>
                        Cadence
                    </Typography>
                    <Typography sx={{ color: '#fff', fontSize: 13, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                        {m.contract?.cadence === 'per_deliverable'
                            ? 'Per Deliverable'
                            : m.contract?.cadence === 'weekly'
                            ? 'Weekly'
                            : 'Monthly'}
                    </Typography>
                </Box>
            </Box>

            {/* Footer: auto-disburse + actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {m.status === 'active' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CurrencyExchangeOutlinedIcon sx={{ fontSize: 13, color: C_MINT }} />
                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            Auto-Disburse On
                        </Typography>
                    </Box>
                ) : (
                    <Box />
                )}
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                    {m.status === 'pending' && (
                        <Box
                            onClick={onResend}
                            sx={{
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 1.5,
                                border: `1px solid rgba(255,201,101,0.3)`,
                                bgcolor: 'rgba(255,201,101,0.07)',
                                color: C_AMBER,
                                fontSize: 11.5,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                cursor: resending ? 'default' : 'pointer',
                                opacity: resending ? 0.6 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                transition: 'all 0.15s',
                                '&:hover': { bgcolor: 'rgba(255,201,101,0.14)' },
                            }}>
                            {resending ? (
                                <CircularProgress size={10} sx={{ color: 'inherit' }} />
                            ) : (
                                <ForwardToInboxOutlinedIcon sx={{ fontSize: 13 }} />
                            )}
                            Resend
                        </Box>
                    )}
                    <Box
                        onClick={onOpen}
                        sx={{
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 1.5,
                            bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                            color: '#fff',
                            fontSize: 11.5,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            transition: 'all 0.15s',
                            '&:hover': { bgcolor: '#2c2c2c' },
                        }}>
                        <EditOutlinedIcon sx={{ fontSize: 13, color: C_AMBER }} /> Manage
                    </Box>
                    <Tooltip title="Remove member">
                        <Box
                            onClick={onRemove}
                            sx={{
                                px: 1.25,
                                py: 0.75,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(255,115,81,0.08)',
                                border: '1px solid rgba(255,115,81,0.2)',
                                color: C_ERROR,
                                fontSize: 11.5,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                transition: 'all 0.15s',
                                '&:hover': { bgcolor: 'rgba(255,115,81,0.15)' },
                            }}>
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                        </Box>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function BrandManageTeamPage() {
    const dispatch = useDispatch();
    const dispatchP = useDispatchPromise();
    const brand = useSelector(activeBrandDashboardBrandSelector);
    const brandId = brand?._id;
    const team = useSelector(brandOpsTeamSelector);
    const fetching = useSelector(brandOpsFetchingTeamSelector);

    // invite form state
    const [email, setEmail] = useState('');
    const [role, setRole] = useState<BrandOpsRole>('content_ops_manager');
    const [picked, setPicked] = useState<IUserSummary[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [contractTitle, setContractTitle] = useState('');
    const [contractFee, setContractFee] = useState('');
    const [contractCadence, setContractCadence] = useState<'weekly' | 'monthly' | 'per_deliverable'>('monthly');
    const [contractCurrency, setContractCurrency] = useState('usd');
    const [contractDeliverables, setContractDeliverables] = useState('');
    const [contractThreshold, setContractThreshold] = useState('2');
    const [contractDuration, setContractDuration] = useState('');
    const [contractScopes, setContractScopes] = useState<string[]>(['organic social']);
    const [requireApproval, setRequireApproval] = useState(true);

    // member management state
    const [drawerMember, setDrawerMember] = useState<IBrandOpsTeamMember | null>(null);
    const [updatingRole, setUpdatingRole] = useState<string | null>(null);
    const [resending, setResending] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);

    // wallet topup state
    const [topupOpen, setTopupOpen] = useState(false);
    const [topupAmountCents, setTopupAmountCents] = useState(FALLBACK_MIN_TOPUP_CENTS);
    const walletDashboard = useSelector(walletDashboardSelector);
    const { fundOptions } = useWalletFundOptions();
    const minTopupCents = getMinWalletTopUpCents(fundOptions) || FALLBACK_MIN_TOPUP_CENTS;
    const shortfallPreset = useMemo(
        () => findMatchingPreset(fundOptions, topupAmountCents),
        [fundOptions, topupAmountCents],
    );

    const activeCount = useMemo(() => team.filter(m => m.status === 'active').length, [team]);
    const pendingCount = useMemo(() => team.filter(m => m.status === 'pending').length, [team]);

    useEffect(() => {
        if (brandId) dispatch(fetchBrandOpsTeam({ brandId, force: true }));
    }, [dispatch, brandId]);

    const invite = async () => {
        if (!brandId) return;
        const selected = picked[0];
        const inviteEmail = selected?.email || email.trim();
        if (!inviteEmail) {
            showToastMessage('Enter an email or pick a user', 'error');
            return;
        }
        const feeNum = parseFloat(contractFee);
        if (!contractFee || isNaN(feeNum) || feeNum <= 0) {
            showToastMessage('Enter a valid contract fee', 'error');
            return;
        }
        setSubmitting(true);
        try {
            await dispatchP(
                inviteBrandOpsTeam({
                    brandId,
                    email: inviteEmail,
                    role,
                    userId: selected?._id,
                    contract: {
                        fee: feeNum,
                        cadence: contractCadence,
                        currency: contractCurrency,
                        title: contractTitle.trim() || undefined,
                        deliverables: contractDeliverables.trim() || undefined,
                        missed_post_threshold: contractThreshold ? Number(contractThreshold) : undefined,
                        clearance_scopes: contractScopes.length ? contractScopes : undefined,
                        require_brand_approval: requireApproval,
                        duration_periods: contractDuration ? Number(contractDuration) : undefined,
                    },
                }),
            );
            showToastMessage('Invite sent with contract', 'success');
            setEmail('');
            setPicked([]);
            setContractTitle('');
            setContractFee('');
            setContractDeliverables('');
            setContractThreshold('2');
            setContractDuration('');
            setContractScopes(['organic social']);
            dispatch(fetchBrandOpsTeam({ brandId, force: true }));
        } catch (err: any) {
            const ins = parseShortfall(err);
            if (ins) {
                setTopupAmountCents(Math.max(ins.shortfall_cents, minTopupCents));
                dispatch(getWalletDashboard());
                setTopupOpen(true);
            } else {
                showToastMessage(err?.message || 'Invite failed — API may be unavailable', 'error');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleTopupComplete = useCallback(() => {
        dispatch(getWalletDashboard());
        setTopupOpen(false);
        showToastMessage('Wallet topped up — you can now send the invite', 'success');
    }, [dispatch]);

    const changeRole = async (memberId: string, newRole: BrandOpsRole) => {
        if (!brandId) return;
        setUpdatingRole(memberId);
        try {
            await dispatchP(updateBrandOpsTeamMember({ brandId, memberId, role: newRole }));
            if (drawerMember?._id === memberId) setDrawerMember(prev => (prev ? { ...prev, role: newRole } : prev));
        } catch (err: any) {
            showToastMessage(err?.message || 'Could not update role', 'error');
        } finally {
            setUpdatingRole(null);
        }
    };

    const resendInvite = async (memberId: string) => {
        if (!brandId) return;
        setResending(memberId);
        try {
            await dispatchP(resendBrandOpsTeamInvite({ brandId, memberId }));
            showToastMessage('Invite resent', 'success');
        } catch (err: any) {
            showToastMessage(err?.message || 'Could not resend invite', 'error');
        } finally {
            setResending(null);
        }
    };

    const confirmRemove = async () => {
        if (!brandId || !deleteTarget) return;
        setDeleting(true);
        try {
            await dispatchP(removeBrandOpsTeamMember({ brandId, memberId: deleteTarget }));
            showToastMessage('Member removed', 'success');
            if (drawerMember?._id === deleteTarget) setDrawerMember(null);
        } catch (err: any) {
            showToastMessage(err?.message || 'Could not remove member', 'error');
        } finally {
            setDeleting(false);
            setDeleteTarget(null);
        }
    };

    return (
        <BrandManageGate>
            <Box sx={MANAGE_PAGE_SX}>
                {/* ── Page header ── */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 2,
                        mb: 4,
                    }}>
                    <Box>
                        <Box
                            component="a"
                            href={ROUTES.BRAND_MANAGE}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mb: 1,
                                color: BRAND_LUXE_MUTED,
                                fontSize: 12,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                textDecoration: 'none',
                                transition: 'color 0.15s',
                                '&:hover': { color: C_AMBER },
                            }}>
                            ← Back to Brand Hub
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.75 }}>
                            <Typography
                                sx={{
                                    color: '#fff',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: { xs: 34, md: 46 },
                                    letterSpacing: '-1px',
                                    lineHeight: 1.05,
                                }}>
                                Team Management
                            </Typography>
                            {brand?.name && (
                                <Box sx={{ px: 1.5, py: 0.3, borderRadius: 5, bgcolor: BRAND_SURFACE_CONTAINER_HIGH }}>
                                    <Typography
                                        sx={{
                                            color: C_AMBER,
                                            fontSize: 11,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            letterSpacing: '0.05em',
                                        }}>
                                        {brand.name}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <Typography
                            sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 14, maxWidth: 520, lineHeight: 1.6 }}>
                            Invite managers, assign Social Manager roles, and configure automated compensation terms
                            with smart milestone clearances.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 2,
                                py: 1,
                                borderRadius: 1.5,
                                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                border: `1px solid ${C_BORDER}`,
                            }}>
                            <LockOutlinedIcon sx={{ fontSize: 16, color: C_MINT }} />
                            <Typography
                                sx={{ color: BRAND_LUXE_MUTED, fontSize: 12, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                Escrow Protected
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* ── Metric cards ── */}
                <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
                    <MetricCard
                        icon={<ManageAccountsOutlinedIcon sx={{ fontSize: 18 }} />}
                        label="Total Members"
                        value={fetching && !team.length ? '…' : team.length}
                        sub={`Seat cap: 8`}
                        accentColor="#64b4ff"
                        trend={team.length > 0 ? `+${team.length} total` : undefined}
                    />
                    <MetricCard
                        icon={<VerifiedOutlinedIcon sx={{ fontSize: 18 }} />}
                        label="Active Personnel"
                        value={activeCount}
                        sub="100% capacity"
                        accentColor={C_MINT}
                    />
                    <MetricCard
                        icon={<ScheduleSendOutlinedIcon sx={{ fontSize: 18 }} />}
                        label="Pending Invites"
                        value={pendingCount}
                        sub={pendingCount === 0 ? 'All cleared' : 'Awaiting acceptance'}
                        accentColor={C_AMBER}
                    />
                </Box>

                {/* ── Main 2-col grid ── */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: '7fr 5fr' },
                        gap: 3,
                        alignItems: 'start',
                    }}>
                    {/* ── LEFT: Invite form ── */}
                    <Box
                        sx={{
                            borderRadius: 3,
                            bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                            border: `1px solid ${C_BORDER}`,
                            overflow: 'hidden',
                        }}>
                        {/* Section header */}
                        <Box
                            sx={{
                                px: 3,
                                py: 2.5,
                                borderBottom: `1px solid ${C_BORDER}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Box
                                        sx={{
                                            width: 9,
                                            height: 9,
                                            borderRadius: '50%',
                                            bgcolor: C_AMBER,
                                            boxShadow: `0 0 8px ${C_AMBER}`,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            color: '#fff',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 20,
                                            letterSpacing: '-0.3px',
                                        }}>
                                        Invite Team Member
                                    </Typography>
                                </Box>
                                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
                                    Deploy an operational seat with pre-filled SLA contracts and automated escrow.
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {/* Member search */}
                            <Box>
                                <FieldLabel>Member Account or Email</FieldLabel>
                                <PeopleSearch
                                    value={picked}
                                    onChange={setPicked}
                                    singleSelection
                                    withProfileSearch
                                    hideCornerClose
                                    selectionAccentColor={C_AMBER}
                                    placeholder="Search by name, handle, or email"
                                />
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Or paste email address directly"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    sx={{ ...brandModalOutlinedInputSx, mt: 1 }}
                                />
                            </Box>

                            {/* Role selector cards */}
                            <Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 1,
                                    }}>
                                    <FieldLabel>Workspace Role</FieldLabel>
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                                    {ROLE_CARDS.map(rc => {
                                        const selected = role === rc.value;
                                        return (
                                            <Box
                                                key={rc.value}
                                                onClick={() => setRole(rc.value)}
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    border: `1px solid ${selected ? `${C_AMBER}55` : C_BORDER}`,
                                                    bgcolor: selected
                                                        ? 'rgba(255,201,101,0.08)'
                                                        : BRAND_SURFACE_CONTAINER_HIGH,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.15s',
                                                    '&:hover': {
                                                        borderColor: `${C_AMBER}44`,
                                                        bgcolor: 'rgba(255,201,101,0.06)',
                                                    },
                                                    position: 'relative',
                                                }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        mb: 0.5,
                                                    }}>
                                                    <Typography
                                                        sx={{
                                                            color: selected ? C_AMBER : '#fff',
                                                            fontSize: 14,
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            letterSpacing: '-0.1px',
                                                        }}>
                                                        {rc.label}
                                                    </Typography>
                                                    {selected && <TaskAltIcon sx={{ fontSize: 16, color: C_AMBER }} />}
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.4)',
                                                        fontSize: 12,
                                                        lineHeight: 1.55,
                                                    }}>
                                                    {rc.sub}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>

                            {/* Retainer & Terms */}
                            <Box
                                sx={{
                                    p: 2.5,
                                    borderRadius: 2,
                                    bgcolor: BRAND_SURFACE_CONTAINER,
                                    border: `1px solid ${C_BORDER}`,
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 2,
                                    }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                        <Box
                                            sx={{
                                                width: 30,
                                                height: 30,
                                                borderRadius: 1.5,
                                                bgcolor: 'rgba(255,201,101,0.12)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                            <Typography sx={{ fontSize: 16 }}>⚖️</Typography>
                                        </Box>
                                        <Box>
                                            <Typography
                                                sx={{
                                                    color: '#fff',
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    fontSize: 16,
                                                    letterSpacing: '-0.2px',
                                                }}>
                                                Retainer &amp; Performance Terms
                                            </Typography>
                                            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
                                                Bound via Thspian Master Services Agreement
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ px: 1.5, py: 0.4, borderRadius: 5, bgcolor: 'rgba(0,253,193,0.1)' }}>
                                        <Typography
                                            sx={{
                                                color: C_MINT,
                                                fontSize: 10,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                letterSpacing: '0.08em',
                                                textTransform: 'uppercase',
                                            }}>
                                            Smart Contract
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                        gap: 1.5,
                                    }}>
                                    {/* Contract title */}
                                    <Box sx={{ gridColumn: '1 / -1' }}>
                                        <FieldLabel>Contract Title</FieldLabel>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="e.g. Q4 Growth & UGC Ops Retainer"
                                            value={contractTitle}
                                            onChange={e => setContractTitle(e.target.value)}
                                            sx={brandModalOutlinedInputSx}
                                        />
                                    </Box>

                                    {/* Monthly rate */}
                                    <Box>
                                        <FieldLabel>Monthly Retainer</FieldLabel>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            type="number"
                                            placeholder="0.00"
                                            value={contractFee}
                                            onChange={e => setContractFee(e.target.value)}
                                            inputProps={{ min: 0, step: '0.01' }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment
                                                        position="start"
                                                        sx={{ color: C_AMBER, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                                        $
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={brandModalOutlinedInputSx}
                                        />
                                    </Box>

                                    {/* Cadence */}
                                    <Box>
                                        <FieldLabel>Payout Cadence</FieldLabel>
                                        <FormControl size="small" fullWidth sx={brandModalOutlinedInputSx}>
                                            <Select
                                                value={contractCadence}
                                                onChange={e =>
                                                    setContractCadence(e.target.value as typeof contractCadence)
                                                }
                                                sx={{ color: '#fff' }}>
                                                <MenuItem value="monthly">Monthly (from signing date)</MenuItem>
                                                <MenuItem value="weekly">Weekly (Fridays)</MenuItem>
                                                <MenuItem value="per_deliverable">Per Deliverable</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>

                                    {/* Currency */}
                                    <Box>
                                        <FieldLabel>Currency</FieldLabel>
                                        <FormControl size="small" fullWidth sx={brandModalOutlinedInputSx}>
                                            <Select
                                                value={contractCurrency}
                                                onChange={e => setContractCurrency(e.target.value)}
                                                sx={{ color: '#fff' }}>
                                                <MenuItem value="usd">USD — US Dollar</MenuItem>
                                                <MenuItem value="ngn">NGN — Nigerian Naira</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>

                                    {/* Missed post threshold */}
                                    <Box>
                                        <FieldLabel>Missed Post SLA Threshold</FieldLabel>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            type="number"
                                            value={contractThreshold}
                                            onChange={e => setContractThreshold(e.target.value)}
                                            sx={brandModalOutlinedInputSx}
                                        />
                                    </Box>

                                    {/* Deliverables */}
                                    <Box sx={{ gridColumn: '1 / -1' }}>
                                        <FieldLabel>Deliverable Commitment</FieldLabel>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="e.g. 12 feed posts + 4 carousels / mo"
                                            value={contractDeliverables}
                                            onChange={e => setContractDeliverables(e.target.value)}
                                            sx={brandModalOutlinedInputSx}
                                        />
                                    </Box>

                                    {/* Duration */}
                                    <Box sx={{ gridColumn: '1 / -1' }}>
                                        <FieldLabel>Duration (periods)</FieldLabel>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            type="number"
                                            placeholder="Leave blank for indefinite"
                                            value={contractDuration}
                                            onChange={e => setContractDuration(e.target.value)}
                                            inputProps={{ min: 1 }}
                                            helperText={
                                                contractDuration
                                                    ? `Auto-ends after ${contractDuration} ${
                                                          contractCadence === 'weekly'
                                                              ? 'week'
                                                              : contractCadence === 'monthly'
                                                              ? 'month'
                                                              : 'deliverable'
                                                      }${Number(contractDuration) > 1 ? 's' : ''}`
                                                    : 'Indefinite — runs until manually voided'
                                            }
                                            FormHelperTextProps={{ sx: { color: BRAND_LUXE_MUTED, fontSize: 11 } }}
                                            sx={brandModalOutlinedInputSx}
                                        />
                                    </Box>
                                </Box>

                                {/* Duration pill */}
                                {!contractDuration && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            p: 1.5,
                                            borderRadius: 1.5,
                                            bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                            mt: 1.5,
                                        }}>
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12 }}>
                                            Contract Duration
                                        </Typography>
                                        <Typography
                                            sx={{ color: C_AMBER, fontSize: 12, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                            Indefinite — runs until voided
                                        </Typography>
                                    </Box>
                                )}

                                {/* Clearance scopes */}
                                <Box sx={{ mt: 2 }}>
                                    <FieldLabel>Clearance &amp; Distribution Scopes</FieldLabel>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                        {['organic social', 'paid amplification', 'whitelisting', 'UGC reuse'].map(
                                            s => {
                                                const active = contractScopes.includes(s);
                                                return (
                                                    <Box
                                                        key={s}
                                                        onClick={() =>
                                                            setContractScopes(prev =>
                                                                prev.includes(s)
                                                                    ? prev.filter(x => x !== s)
                                                                    : [...prev, s],
                                                            )
                                                        }
                                                        sx={{
                                                            px: 1.5,
                                                            py: 0.65,
                                                            borderRadius: 5,
                                                            cursor: 'pointer',
                                                            border: `1px solid ${active ? `${C_AMBER}55` : C_BORDER}`,
                                                            bgcolor: active ? 'rgba(255,201,101,0.12)' : 'transparent',
                                                            transition: 'all 0.15s',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            '&:hover': {
                                                                bgcolor: 'rgba(255,201,101,0.07)',
                                                                borderColor: `${C_AMBER}33`,
                                                            },
                                                        }}>
                                                        {active && (
                                                            <TaskAltIcon sx={{ fontSize: 11, color: C_AMBER }} />
                                                        )}
                                                        <Typography
                                                            sx={{
                                                                fontSize: 12,
                                                                color: active ? C_AMBER : BRAND_LUXE_MUTED,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            }}>
                                                            {s}
                                                        </Typography>
                                                    </Box>
                                                );
                                            },
                                        )}
                                    </Box>
                                </Box>

                                {/* Require approval */}
                                <Box sx={{ mt: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={requireApproval}
                                                onChange={e => setRequireApproval(e.target.checked)}
                                                size="small"
                                                sx={{ color: BRAND_LUXE_MUTED, '&.Mui-checked': { color: C_AMBER } }}
                                            />
                                        }
                                        label={
                                            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
                                                Require brand executive approval after manager signs
                                            </Typography>
                                        }
                                    />
                                </Box>
                            </Box>

                            {/* CTA */}
                            <ManageGoldButton
                                onClick={invite}
                                disabled={submitting}
                                sx={{
                                    py: 1.75,
                                    borderRadius: 5,
                                    gap: 1,
                                    width: '100%',
                                    justifyContent: 'center',
                                    fontSize: 14,
                                }}>
                                {submitting && <CircularProgress size={14} sx={{ color: 'inherit' }} />}
                                {submitting ? 'Generating Contract…' : 'Send Invite with Retainer Contract'}
                                {!submitting && <ArrowForwardIcon sx={{ fontSize: 18 }} />}
                            </ManageGoldButton>
                        </Box>
                    </Box>

                    {/* ── RIGHT: Members list ── */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Members section */}
                        <Box
                            sx={{
                                borderRadius: 3,
                                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                border: `1px solid ${C_BORDER}`,
                                overflow: 'hidden',
                            }}>
                            <Box
                                sx={{
                                    px: 3,
                                    py: 2.5,
                                    borderBottom: `1px solid ${C_BORDER}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}>
                                <Box>
                                    <Typography
                                        sx={{
                                            color: '#fff',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 20,
                                            letterSpacing: '-0.3px',
                                        }}>
                                        Active Team Members {fetching ? '' : `(${team.length})`}
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
                                        Live contracts currently active in {brand?.name || 'this workspace'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {fetching && !team.length ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress size={24} sx={{ color: C_AMBER }} />
                                    </Box>
                                ) : !team.length ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <ManageAccountsOutlinedIcon
                                            sx={{ fontSize: 40, color: 'rgba(255,255,255,0.1)', mb: 1 }}
                                        />
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13 }}>
                                            No team members yet. Use the form to invite someone.
                                        </Typography>
                                    </Box>
                                ) : (
                                    team.map(m => (
                                        <MemberCard
                                            key={m._id}
                                            member={m}
                                            resending={resending === m._id}
                                            onOpen={() => setDrawerMember(m)}
                                            onResend={() => resendInvite(m._id)}
                                            onRemove={() => setDeleteTarget(m._id)}
                                        />
                                    ))
                                )}
                            </Box>
                        </Box>

                        {/* Smart contract info block */}
                        <Box
                            sx={{
                                p: 2.5,
                                borderRadius: 2,
                                bgcolor: BRAND_SURFACE_CONTAINER,
                                border: `1px solid ${C_BORDER}`,
                                display: 'flex',
                                gap: 1.5,
                            }}>
                            <GavelOutlinedIcon sx={{ fontSize: 20, color: C_AMBER, flexShrink: 0, mt: 0.2 }} />
                            <Box>
                                <Typography
                                    sx={{ color: '#fff', fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, mb: 0.5 }}>
                                    Smart Contract Automation
                                </Typography>
                                <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12, lineHeight: 1.6 }}>
                                    All invited managers receive a secure sign-off link. Retainers are held in milestone
                                    escrow until deliverable thresholds are satisfied.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* ── Member detail drawer ── */}
            <Drawer
                anchor="right"
                open={Boolean(drawerMember)}
                onClose={() => setDrawerMember(null)}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 360 },
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        backgroundImage: 'none',
                        borderLeft: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                        p: 3,
                    },
                }}>
                {drawerMember &&
                    (() => {
                        const m = drawerMember;
                        const roleMeta = ROLE_META[m.role] ?? ROLE_META.viewer;
                        const displayName =
                            [m.user?.firstName, m.user?.lastName].filter(Boolean).join(' ') ||
                            m.user?.userName ||
                            m.email;
                        return (
                            <>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 3,
                                    }}>
                                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 16 }}>
                                        Member
                                    </Typography>
                                    <IconButton onClick={() => setDrawerMember(null)} sx={{ color: BRAND_LUXE_MUTED }}>
                                        <CloseIcon />
                                    </IconButton>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                                    <Avatar
                                        src={m.user?.profile_picture}
                                        sx={{
                                            width: 72,
                                            height: 72,
                                            mb: 1.5,
                                            bgcolor: roleMeta.bg,
                                            color: roleMeta.color,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 28,
                                            border: `3px solid ${roleMeta.color}55`,
                                        }}>
                                        {(m.user?.firstName || m.user?.userName || m.email || '?')[0]?.toUpperCase()}
                                    </Avatar>
                                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 18 }}>
                                        {displayName}
                                    </Typography>
                                    {m.user?.userName && (
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12, mt: 0.25 }}>
                                            @{m.user.userName}
                                        </Typography>
                                    )}
                                    <Box sx={{ mt: 1 }}>
                                        <StatusChip
                                            label={m.status}
                                            tone={
                                                m.status === 'active'
                                                    ? 'mint'
                                                    : m.status === 'pending'
                                                    ? 'gold'
                                                    : 'muted'
                                            }
                                        />
                                    </Box>
                                </Box>

                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 2 }} />

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                        <EmailOutlinedIcon sx={{ fontSize: 16, color: BRAND_LUXE_MUTED }} />
                                        <Typography
                                            sx={{
                                                color: BRAND_LUXE_MUTED,
                                                fontSize: 12,
                                                flex: 1,
                                                wordBreak: 'break-all',
                                            }}>
                                            {m.email}
                                        </Typography>
                                    </Box>
                                    {m.invited_at && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                            <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: BRAND_LUXE_MUTED }} />
                                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12 }}>
                                                Invited {new Date(m.invited_at).toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                    )}
                                    {m.accepted_at && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                            <LockOutlinedIcon sx={{ fontSize: 16, color: C_MINT }} />
                                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12 }}>
                                                Accepted {new Date(m.accepted_at).toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 2 }} />

                                <FieldLabel>Role</FieldLabel>
                                <Select
                                    size="small"
                                    fullWidth
                                    value={m.role}
                                    disabled={updatingRole === m._id}
                                    onChange={e => changeRole(m._id, e.target.value as BrandOpsRole)}
                                    sx={{
                                        color: '#fff',
                                        mb: 3,
                                        fontSize: 13,
                                        '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(255,255,255,0.3)',
                                        },
                                        '.MuiSvgIcon-root': { color: BRAND_LUXE_MUTED },
                                    }}>
                                    {ROLES.map(r => (
                                        <MenuItem key={r.value} value={r.value} sx={{ fontSize: 13 }}>
                                            {r.label}
                                        </MenuItem>
                                    ))}
                                </Select>

                                {m.status === 'pending' && (
                                    <ManageGoldButton
                                        fullWidth
                                        disabled={resending === m._id}
                                        onClick={() => resendInvite(m._id)}
                                        sx={{
                                            mb: 1.5,
                                            bgcolor: 'transparent',
                                            color: C_AMBER,
                                            border: '1px solid rgba(255,201,101,0.35)',
                                        }}>
                                        {resending === m._id ? 'Sending…' : 'Resend invite'}
                                    </ManageGoldButton>
                                )}
                                <Box
                                    component="button"
                                    onClick={() => {
                                        setDeleteTarget(m._id);
                                        setDrawerMember(null);
                                    }}
                                    sx={{
                                        width: '100%',
                                        py: 1,
                                        borderRadius: 1.5,
                                        border: '1px solid rgba(255,80,60,0.25)',
                                        bgcolor: 'rgba(255,80,60,0.06)',
                                        color: C_ERROR,
                                        fontSize: 13,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.75,
                                        '&:hover': { bgcolor: 'rgba(255,80,60,0.12)' },
                                    }}>
                                    <DeleteOutlineIcon sx={{ fontSize: 16 }} /> Remove member
                                </Box>
                            </>
                        );
                    })()}
            </Drawer>

            {/* ── Delete confirm ── */}
            <Dialog
                open={Boolean(deleteTarget)}
                onClose={() => !deleting && setDeleteTarget(null)}
                PaperProps={{ sx: { bgcolor: '#1a1a2e', color: '#fff', borderRadius: 2 } }}>
                <DialogTitle sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16 }}>Remove member?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: BRAND_LUXE_MUTED, fontSize: 13 }}>
                        This will revoke their access to Social Manager. This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setDeleteTarget(null)}
                        disabled={deleting}
                        sx={{ color: BRAND_LUXE_MUTED, textTransform: 'none', fontSize: 13 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmRemove}
                        disabled={deleting}
                        sx={{ color: C_ERROR, textTransform: 'none', fontSize: 13, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                        {deleting ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : 'Remove'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Wallet top-up modal ── */}
            <Dialog
                open={topupOpen}
                onClose={() => setTopupOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        backgroundImage: 'none',
                        borderRadius: 3,
                        border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                    },
                }}>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 18,
                        color: '#fff',
                        pb: 1,
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20, color: C_AMBER }} />
                        Top up wallet to continue
                    </Box>
                    <IconButton
                        onClick={() => setTopupOpen(false)}
                        size="small"
                        sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 14,
                                color: 'rgba(255,255,255,0.75)',
                                lineHeight: 1.6,
                            }}>
                            Your wallet doesn't have enough funds. Add at least{' '}
                            <strong style={{ color: C_AMBER }}>{formatUsdCents(topupAmountCents)}</strong> to cover the
                            contract rate shortfall, then retry the invite.
                        </Typography>
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                bgcolor: 'rgba(0,0,0,0.2)',
                            }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    color: 'rgba(255,255,255,0.45)',
                                    mb: 0.5,
                                }}>
                                Current wallet balance
                            </Typography>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20, color: C_MINT }}>
                                {formatUsdCents(walletDashboard?.available_funds_cents ?? 0)}
                            </Typography>
                        </Box>
                        <WalletPaymentSection
                            amountCents={topupAmountCents}
                            activePreset={shortfallPreset ?? null}
                            selectedPreset={shortfallPreset ?? null}
                            fundOptions={fundOptions}
                            onPaymentComplete={handleTopupComplete}
                        />
                    </Box>
                </DialogContent>
            </Dialog>
        </BrandManageGate>
    );
}
